import { useState } from 'react';
import { useParams } from 'react-router';

import {
  useGetClinVarHotspotDataQuery,
  useGetClinVarHotspotVariantsQuery,
  useGetVariantDetailQuery,
} from '../../../api/variantApi';
import { VARIANT_CONSEQUENCE_TYPES } from '../../constants';

export type ClinVarHotspotVariantLink = {
  clinvar_id?: string;
  name?: string;
};

export type ClinVarHotspotSummaryRow = {
  key: string;
  b_lb_variant: ClinVarHotspotVariantLink;
  distance?: number;
  region_size?: number;
  missense_count?: number;
  missense_p_lp_count?: number;
  missense_other_count?: number;
};

// Consequences for which ClinVar hotspot data is calculated on the backend.
// Shared by both hotspot queries below to avoid duplicating the check.
const HOTSPOT_ELIGIBLE_CONSEQUENCES: string[] = [
  VARIANT_CONSEQUENCE_TYPES.MISSENSE,
  VARIANT_CONSEQUENCE_TYPES.INFRAME_DELETION,
  VARIANT_CONSEQUENCE_TYPES.INFRAME_INSERTION,
];

const isHotspotEligibleConsequence = (consequence?: string): boolean =>
  consequence !== undefined &&
  HOTSPOT_ELIGIBLE_CONSEQUENCES.includes(consequence);

/**
 * Custom hook that encapsulates all the business logic for the ClinVar
 * hotspot section: data fetching, the collapsible variants table state,
 * and the derived summary table data source.
 */
export const useClinVarHotspotLogic = () => {
  const params = useParams();

  const { data: variant } = useGetVariantDetailQuery(
    { variant_id: params.vid },
    { skip: !params.vid },
  );

  const isEligibleForHotspot = isHotspotEligibleConsequence(
    variant?.consequence,
  );

  const { data: clinVarHotspotData, isLoading: isClinVarHotspotDataLoading } =
    useGetClinVarHotspotDataQuery(
      { variant_id: params.vid },
      { skip: !params.vid || !isEligibleForHotspot },
    );

  const [showMore, setShowMore] = useState(false);
  const {
    data: clinVarHotspotVariants,
    isLoading: isClinVarHotspotVariantsLoading,
  } = useGetClinVarHotspotVariantsQuery(
    { variant_id: params.vid },
    {
      skip: !showMore || !params.vid || !isEligibleForHotspot,
    },
  );

  const handleShowMoreClick = () => {
    setShowMore((v) => !v);
  };

  const clinVarHotspotDataSource: ClinVarHotspotSummaryRow[] = [
    {
      key: '1',
      b_lb_variant: {
        clinvar_id: clinVarHotspotData?.downstream_variant_clinvar_id,
        name: clinVarHotspotData?.downstream_variant_name,
      },
      distance: clinVarHotspotData?.downstream_distance,
      region_size: clinVarHotspotData?.region_size,
      missense_count: clinVarHotspotData?.total_variants,
      missense_p_lp_count: clinVarHotspotData?.p_lp_variants,
      missense_other_count:
        (clinVarHotspotData?.total_variants ?? 0) -
        (clinVarHotspotData?.p_lp_variants ?? 0),
    },
    {
      key: '2',
      b_lb_variant: {
        clinvar_id: clinVarHotspotData?.upstream_variant_clinvar_id,
        name: clinVarHotspotData?.upstream_variant_name,
      },
      distance: clinVarHotspotData?.upstream_distance,
      region_size: 0,
      missense_count: 0,
      missense_p_lp_count: 0,
      missense_other_count: 0,
    },
  ];

  return {
    clinVarHotspotData,
    isClinVarHotspotDataLoading,
    clinVarHotspotVariants,
    isClinVarHotspotVariantsLoading,
    clinVarHotspotDataSource,
    showMore,
    handleShowMoreClick,
  };
};

export default useClinVarHotspotLogic;
